import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Download, ChevronDown, ChevronRight, Calendar, Clock,
  FileText, CheckCircle, AlertCircle, Eye, Printer, X,
  CreditCard, Star, MessageSquare, Check, Shield,
  Banknote, Car, MapPin, QrCode, RefreshCw
} from 'lucide-react';
import { useAuth }                      from '../context/AuthContext';
import { getMyBookings, getMyInvoice } from '../api';
import { downloadInvoicePdf, printInvoicePdf } from '../utils/PdfInvoice';
import { getFeedbackByBooking, savePayment, saveFeedback } from '../store';
import './ServiceRecordsPage.css';

/* ── Status map ────────────────────────────────────────── */
const SM = {
  completed:            { label:'Completed',             badge:'badge-success', Icon:CheckCircle },
  COMPLETED:            { label:'Completed',             badge:'badge-success', Icon:CheckCircle },
  'in-progress':        { label:'In Progress',           badge:'badge-warning', Icon:Clock       },
  in_progress:          { label:'In Progress',           badge:'badge-warning', Icon:Clock       },
  IN_PROGRESS:          { label:'In Progress',           badge:'badge-warning', Icon:Clock       },
  confirmed:            { label:'Confirmed',             badge:'badge-info',    Icon:CheckCircle },
  CONFIRMED:            { label:'Confirmed',             badge:'badge-info',    Icon:CheckCircle },
  waiting:              { label:'Waiting',               badge:'badge-info',    Icon:Clock       },
  WAITING:              { label:'Waiting',               badge:'badge-info',    Icon:Clock       },
  pending:              { label:'Pending',               badge:'badge-info',    Icon:AlertCircle },
  pending_confirmation: { label:'Awaiting Confirmation', badge:'badge-neutral', Icon:AlertCircle },
  PENDING_CONFIRMATION: { label:'Awaiting Confirmation', badge:'badge-neutral', Icon:AlertCircle },
  cancelled:            { label:'Cancelled',             badge:'badge-neutral', Icon:AlertCircle },
  CANCELLED:            { label:'Cancelled',             badge:'badge-neutral', Icon:AlertCircle },
  rejected:             { label:'Rejected',              badge:'badge-danger',  Icon:AlertCircle },
  REJECTED:             { label:'Rejected',              badge:'badge-danger',  Icon:AlertCircle },
};

const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN');

function calcRecord(rec) {
  if (rec.invoiceLoaded) {
    const subtotal  = Number(rec.subtotal  || 0);
    const gstAmount = Number(rec.gstAmount || 0);
    const grand     = Number(rec.grandTotal || subtotal + gstAmount);
    return { subtotal, gstAmount, grand, gst: rec.gst ?? 18 };
  }
  const subtotal  = Number(rec.amount || 0);
  const gst       = rec.gst ?? 18;
  const gstAmount = Math.round(subtotal * gst / 100);
  return { subtotal, gstAmount, grand: subtotal + gstAmount, gst };
}

/* ── Razorpay SDK loader ────────────────────────────────── */
// Use your own test key from https://dashboard.razorpay.com → Settings → API Keys
// rzp_test_1DP5mmOlF5G5ag is Razorpay's official demo key (works for testing)
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_1DP5mmOlF5G5ag';

function loadRazorpaySdk() {
  return new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    // Remove any stale/broken script tags first
    const existing = document.querySelector('script[src*="razorpay"]');
    if (existing) existing.remove();
    const s = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async   = true;
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/* ══════════════════════════════════════════════════════════
   INVOICE VIEW MODAL
══════════════════════════════════════════════════════════ */
function InvoiceModal({ rec, user, onClose, onPay }) {
  const { subtotal, grand, gst } = calcRecord(rec);
  /* Payment is considered done ONLY if admin marked it paid on the backend */
  const isPaid = rec.invoicePaid;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={e => e.stopPropagation()}>

        {/* sticky header */}
        <div className="inv-modal__header">
          <div className="inv-modal__header-left">
            <div className="inv-modal__brand">AutoElite</div>
            <div className="inv-modal__invoice-no">{rec.invoiceId || rec.id}</div>
          </div>
          <div className="inv-modal__header-right">
            <button className="inv-icon-btn" onClick={() => downloadInvoicePdf(rec, user)}>
              <Download size={14}/><span>PDF</span>
            </button>
            <button className="inv-icon-btn" onClick={() => printInvoicePdf(rec, user)}>
              <Printer size={14}/><span>Print</span>
            </button>
            {/* Pay Now only shows if admin has NOT yet marked it paid */}
            {!isPaid && (
              <button className="btn btn-primary btn-sm" onClick={() => { onClose(); onPay(rec); }}>
                <CreditCard size={13}/> Pay Now
              </button>
            )}
            {isPaid && (
              <span className="inv-paid-chip"><Check size={12}/> PAID</span>
            )}
            <button className="inv-close-btn" onClick={onClose}><X size={18}/></button>
          </div>
        </div>

        <div className="inv-modal__body">
          {/* header band */}
          <div className="inv-top-band">
            <div className="inv-workshop">
              <div className="inv-workshop__name">AutoElite</div>
              <div className="inv-workshop__addr">12, GT Road, Ludhiana, Punjab 141001</div>
              <div className="inv-workshop__contact">+91 12345 67890 · hello@autoelite.in</div>
              <div className="inv-workshop__gstin">GSTIN: 03AABCU9603R1ZV</div>
            </div>
            <div className="inv-meta">
              <div className="inv-meta__row"><span>Invoice</span><strong>{rec.invoiceId || rec.id}</strong></div>
              <div className="inv-meta__row"><span>Date</span><strong>{rec.date}</strong></div>
              <div className="inv-meta__row"><span>Status</span>
                <span className={`inv-status-pill ${isPaid ? 'inv-status-pill--paid' : 'inv-status-pill--due'}`}>
                  {isPaid ? 'PAID' : 'UNPAID'}
                </span>
              </div>
            </div>
          </div>

          {/* parties */}
          <div className="inv-parties">
            <div className="inv-party">
              <div className="inv-party__label">BILL TO</div>
              <div className="inv-party__name">{user?.name || 'Customer'}</div>
              {user?.phone && <div className="inv-party__detail">{user.phone}</div>}
              {user?.email && <div className="inv-party__detail">{user.email}</div>}
            </div>
            <div className="inv-party">
              <div className="inv-party__label">VEHICLE</div>
              <div className="inv-party__name">{rec.vehicle}</div>
              <div className="inv-party__detail">Reg. No: <strong>{rec.regNo}</strong></div>
              {rec.mechanic && <div className="inv-party__detail">Mechanic: {rec.mechanic}</div>}
            </div>
          </div>

          {/* line items */}
          <table className="inv-table">
            <thead>
              <tr>
                <th className="inv-th inv-th--desc">Description</th>
                <th className="inv-th inv-th--center">Qty</th>
                <th className="inv-th inv-th--right">Rate</th>
                <th className="inv-th inv-th--right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(rec.services || []).map((s, i) => {
                const isObj = typeof s === 'object' && s !== null;
                return (
                  <tr key={i} className={i % 2 === 0 ? 'inv-tr--alt' : ''}>
                    <td className="inv-td">
                      <span className="inv-item-name">{isObj ? s.name : s}</span>
                      <span className="inv-item-badge">Service</span>
                    </td>
                    <td className="inv-td inv-td--center">1</td>
                    <td className="inv-td inv-td--right">{isObj ? inr(s.unitPrice) : '—'}</td>
                    <td className="inv-td inv-td--right inv-td--strong">{isObj ? inr(s.amount) : '—'}</td>
                  </tr>
                );
              })}
              {(rec.spareParts || []).map((p, i) => (
                <tr key={p.name || i} className={(rec.services.length + i) % 2 === 0 ? 'inv-tr--alt' : ''}>
                  <td className="inv-td">
                    <span className="inv-item-name">{p.name}</span>
                    <span className="inv-item-badge inv-item-badge--part">Part</span>
                  </td>
                  <td className="inv-td inv-td--center">{p.qty}</td>
                  <td className="inv-td inv-td--right">{inr(p.unitPrice)}</td>
                  <td className="inv-td inv-td--right inv-td--strong">{inr(p.lineTotal || p.qty * p.unitPrice)}</td>
                </tr>
              ))}
              {Number(rec.labour || 0) > 0 && (
                <tr>
                  <td className="inv-td"><span className="inv-item-name">Labour Charges</span></td>
                  <td className="inv-td inv-td--center">1</td>
                  <td className="inv-td inv-td--right">{inr(rec.labour)}</td>
                  <td className="inv-td inv-td--right inv-td--strong">{inr(rec.labour)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* totals */}
          <div className="inv-totals-wrap">
            <div className="inv-totals">
              <div className="inv-total-row"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {/* Split GST: parts 18% fixed, services use invoice rate */}
              {(() => {
                const pTot  = (rec.spareParts||[]).reduce((s,p)=>s+Number(p.lineTotal||p.qty*p.unitPrice||0),0);
                const slBase= subtotal - pTot;
                const sGst  = Math.round(slBase * (gst||18) / 100);
                const pGst  = Math.round(pTot * 18 / 100);
                return (
                  <>
                    <div className="inv-total-row" style={{ fontSize:12, color:'var(--text-muted)' }}>
                      <span>GST on Services &amp; Labour ({gst}%)</span><span>{inr(sGst)}</span>
                    </div>
                    {pTot > 0 && (
                      <div className="inv-total-row" style={{ fontSize:12, color:'var(--text-muted)' }}>
                        <span>GST on Spare Parts (18%)</span><span>{inr(pGst)}</span>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="inv-total-grand"><span>Grand Total</span><span>{inr(grand)}</span></div>
            </div>
          </div>

          {rec.notes && (
            <div className="inv-notes">
              <div className="inv-notes__title">Mechanic Notes</div>
              <div className="inv-notes__text">{rec.notes}</div>
            </div>
          )}

          {/* Payment confirmed banner — only when admin has marked paid */}
          {isPaid && (
            <div className="inv-paid-stamp">
              <Check size={14}/>
              Payment confirmed by AutoElite workshop
            </div>
          )}

          {/* Unpaid notice */}
          {!isPaid && (
            <div className="inv-unpaid-notice">
              <AlertCircle size={14}/>
              Payment pending — pay online or at the workshop to clear this invoice.
            </div>
          )}

          <div className="inv-footer">
            Thank you for choosing AutoElite · Certified Workshop · ISO 9001:2015<br/>
            <span style={{ fontSize:10 }}>Computer-generated invoice. No signature required.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAYMENT MODAL
   Payment options:
     1. Online via Razorpay (real SDK integration)
     2. Pay at Workshop — records intent, status stays UNPAID
        until admin marks it paid from their dashboard.
══════════════════════════════════════════════════════════ */
function PaymentModal({ rec, user, onClose, onSuccess }) {
  const { grand } = calcRecord(rec);
  const [step,    setStep]    = useState('choose');
  const [txnId,   setTxnId]   = useState('');
  const [rzpErr,  setRzpErr]  = useState('');
  const [loading, setLoading] = useState(false);

  const isPickup =
    rec.pickupType === 'VEHICLE_PICKUP' ||
    rec.pickup     === 'Vehicle Pickup'  ||
    rec.pickupType === 'Vehicle Pickup';

  /* ── Razorpay ─────────────────────────────────────────── */
  const handleRazorpay = async () => {
    setRzpErr('');
    setLoading(true);
    // Stay on 'choose' screen — do NOT switch to 'rzp-loading' yet.
    // The loading state just disables the button while SDK loads.

    let loaded = false;
    try {
      loaded = await loadRazorpaySdk();
    } catch {
      loaded = false;
    }

    if (!loaded || !window.Razorpay) {
      setRzpErr('Razorpay could not load — check your internet connection and try again.');
      setLoading(false);
      return;
    }

    // Razorpay requires amount > 0 in paise. For demo/test use minimum ₹1 (100 paise).
    const amountPaise = grand > 0 ? Math.round(grand * 100) : 100;

    const options = {
      key:         RAZORPAY_KEY,
      amount:      amountPaise,   // in paise — must be > 0
      currency:    'INR',
      name:        'AutoElite',
      description: `AutoElite Service - ${(rec.invoiceId || rec.id || 'Booking').replace(/[^a-zA-Z0-9\s-]/g, '')}`,
      // image: '/logo192.png',  // removed — missing image causes Razorpay init error
      prefill: {
        name:    user?.name  || '',
        email:   user?.email || '',
        // Razorpay requires exactly 10 digits, no country code
        contact: (user?.phone || '').replace(/\D/g, '').slice(-10),
      },
      notes: {
        booking_id: String(rec.id),
        invoice_id: String(rec.invoiceId || ''),
      },
      theme: { color: '#005DCC' },

      /* ── Success handler — called by Razorpay after payment ── */
      handler(response) {
        const txn = response.razorpay_payment_id;
        setTxnId(txn);
        savePayment({
          invoiceId:     rec.invoiceId || rec.id,
          bookingId:     rec.id,
          method:        'Razorpay Online',
          amount:        grand,
          transactionId: txn,
          userId:        user?.id,
          paidAt:        new Date().toISOString(),
        });
        setLoading(false);
        setStep('done');
        onSuccess({ txnId: txn, method: 'Razorpay Online' });
      },

      modal: {
        /* Called when user closes the Razorpay popup without paying */
        ondismiss() {
          setLoading(false);
          setStep('choose');
          setRzpErr('Payment was cancelled. You can try again anytime.');
        },
        escape:       true,
        backdropclose: false,
        animation:    true,
      },
    };

    try {
      const rzp = new window.Razorpay(options);

      /* Failed payment (wrong OTP, card declined, etc.) */
      rzp.on('payment.failed', res => {
        const desc = res?.error?.description || res?.error?.reason || 'Payment declined';
        setRzpErr(`Payment failed: ${desc}. Please try a different payment method.`);
        setLoading(false);
        setStep('choose');
      });

      // Open the Razorpay checkout popup.
      // After open() returns the popup is visible — switch to rzp-loading
      // so our modal shows a "waiting" background behind Razorpay's own UI.
      rzp.open();
      setStep('rzp-loading');
      // NOTE: setLoading(false) is called inside handler / ondismiss / payment.failed
      // Do NOT call it here — that would re-enable the button while popup is open.

    } catch (err) {
      // rzp.open() itself threw — likely a CSP block or key error
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('key')) {
        setRzpErr('Invalid Razorpay key. Contact AutoElite support.');
      } else {
        setRzpErr('Could not open payment window. Please refresh the page and try again.');
      }
      setLoading(false);
      setStep('choose');
    }
  };

  /* ── Pay at workshop / on delivery ───────────────────── */
  const handleOfflineConfirm = () => {
    setStep('processing');
    setTimeout(() => {
      const ref = 'WRK-' + Date.now().toString().slice(-8);
      setTxnId(ref);
      // Record the intent locally only — status stays UNPAID until admin confirms
      savePayment({
        invoiceId:     rec.invoiceId || rec.id,
        bookingId:     rec.id,
        method:        isPickup ? 'Pay on Delivery' : 'Pay at Workshop',
        amount:        grand,
        transactionId: ref,
        userId:        user?.id,
        paidAt:        new Date().toISOString(),
        pendingAdminConfirmation: true,
      });
      setStep('done');
      onSuccess({ txnId: ref, method: isPickup ? 'Pay on Delivery' : 'Pay at Workshop', pendingAdmin: true });
    }, 1000);
  };

  const BackBtn = ({ to }) => (
    <button className="pay-back-btn" onClick={() => setStep(to)}>
      <ChevronRight size={15} style={{ transform:'rotate(180deg)' }}/>
    </button>
  );

  const Header = ({ title, sub, backTo }) => (
    <div className="pay-modal__header">
      <div className="pay-modal__header-left">
        {backTo && <BackBtn to={backTo}/>}
        <div>
          <h3 className="pay-modal__title">{title}</h3>
          {sub && <p className="pay-modal__sub">{sub}</p>}
        </div>
      </div>
      <button className="inv-close-btn" onClick={onClose}><X size={18}/></button>
    </div>
  );

  const AmountBanner = () => (
    <div className="pay-amount-banner">
      <div className="pay-amount-banner__label"><Shield size={14}/> Secure Payment</div>
      <div className="pay-amount-banner__amount">{inr(grand)}</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={step === 'done' ? onClose : undefined}>
      <div className="pay-modal" onClick={e => e.stopPropagation()}>

        {/* ── STEP: choose ── */}
        {step === 'choose' && (
          <>
            <Header
              title="Pay Invoice"
              sub={`${rec.invoiceId || rec.id} · ${rec.vehicle || ''}`}
            />
            <AmountBanner/>

            {rzpErr && (
              <div className="pay-error-banner">
                <AlertCircle size={14}/> {rzpErr}
              </div>
            )}

            {isPickup && (
              <div className="pay-pickup-notice">
                <div className="pay-pickup-notice__icon"><Car size={18}/></div>
                <div>
                  <p className="pay-pickup-notice__title">Vehicle Pickup Booking</p>
                  <p className="pay-pickup-notice__desc">
                    Your car will be dropped back to your address by our driver.
                    {rec.address && <><br/><strong>{rec.address}</strong></>}
                  </p>
                </div>
              </div>
            )}

            <p className="pay-section-label">How would you like to pay?</p>

            <div className="pay-methods">
              {/* Razorpay */}
              <button
                className="pay-method-card pay-method-card--razorpay"
                onClick={handleRazorpay}
                disabled={loading}
              >
                <div className="pay-method-card__icon pay-method-card__icon--rzp">
                  <svg width="24" height="24" viewBox="0 0 50 50" fill="none">
                    <polygon points="5,40 20,10 38,22 30,28 42,45" fill="#3395FF"/>
                    <polygon points="20,10 38,22 30,28 22,12" fill="#002A7B"/>
                  </svg>
                </div>
                <div className="pay-method-card__text">
                  <div className="pay-method-card__label">Pay Online via Razorpay</div>
                  <div className="pay-method-card__desc">UPI · Cards · Net Banking · Wallets</div>
                </div>
                <span className="pay-rzp-badge">Instant</span>
                <ChevronRight size={16} className="pay-method-card__arrow"/>
              </button>

              {/* Offline */}
              <button
                className="pay-method-card"
                onClick={() => setStep(isPickup ? 'confirm-delivery' : 'confirm-workshop')}
              >
                <div className="pay-method-card__icon">
                  {isPickup ? <Car size={20}/> : <Banknote size={20}/>}
                </div>
                <div className="pay-method-card__text">
                  <div className="pay-method-card__label">
                    {isPickup ? 'Pay on Delivery' : 'Pay at Service Centre'}
                  </div>
                  <div className="pay-method-card__desc">
                    {isPickup
                      ? 'Pay cash or scan QR when our driver delivers your car'
                      : 'Pay cash or card when you visit the workshop'}
                  </div>
                </div>
                <ChevronRight size={16} className="pay-method-card__arrow"/>
              </button>
            </div>

            <div className="pay-secure-note">
              <Shield size={11}/> 256-bit SSL · PCI-DSS Compliant · Your data is safe
            </div>
          </>
        )}

        {/* ── STEP: rzp-loading — shown AFTER rzp.open() succeeds ── */}
        {step === 'rzp-loading' && (
          <div className="pay-rzp-loading">
            <div className="pay-rzp-loading__logo">
              <svg width="48" height="48" viewBox="0 0 50 50" fill="none">
                <polygon points="5,40 20,10 38,22 30,28 42,45" fill="#3395FF"/>
                <polygon points="20,10 38,22 30,28 22,12" fill="#002A7B"/>
              </svg>
            </div>
            <p className="pay-rzp-loading__title">Razorpay Checkout Open</p>
            <p className="pay-rzp-loading__sub">
              Complete your payment in the Razorpay window above.
            </p>
            {/* Test card hint — remove in production */}
            <div className="pay-rzp-testcard">
              <p className="pay-rzp-testcard__label">🧪 Test Mode — Use these details:</p>
              <p className="pay-rzp-testcard__row"><span>Card</span><code>5267 3181 8797 5449</code></p>
              <p className="pay-rzp-testcard__row"><span>Expiry</span><code>12/26</code></p>
              <p className="pay-rzp-testcard__row"><span>CVV</span><code>123</code></p>
              <p className="pay-rzp-testcard__row"><span>OTP</span><code>1234</code></p>
              <p className="pay-rzp-testcard__row"><span>Or UPI</span><code>success@razorpay</code></p>
            </div>
            <div className="pay-rzp-loading__dots"><span/><span/><span/></div>
            <p className="pay-rzp-loading__cancel"
              onClick={() => { setLoading(false); setRzpErr('Payment cancelled.'); setStep('choose'); }}>
              ← Back / Cancel
            </p>
          </div>
        )}

        {/* ── STEP: confirm-workshop ── */}
        {step === 'confirm-workshop' && (
          <>
            <Header title="Pay at Service Centre" backTo="choose"/>
            <div className="pay-form-body">
              <div className="pay-offline-screen">
                <div className="pay-offline-icon pay-offline-icon--green"><Banknote size={34}/></div>
                <h3 className="pay-offline-title">Pay at the Workshop</h3>
                <p className="pay-offline-desc">
                  Pay by cash or card when you visit our service centre.
                  Your invoice will remain <strong>UNPAID</strong> until our team confirms receipt.
                </p>
                <div className="pay-offline-amount-chip">
                  <span>Amount Due</span>
                  <strong>{inr(grand)}</strong>
                </div>
                <div className="pay-invoice-notice">
                  <AlertCircle size={15}/>
                  <div>
                    <p className="pay-invoice-notice__title">Status updates after admin confirmation</p>
                    <p className="pay-invoice-notice__desc">
                      Once you pay at the counter and our team processes it,
                      your invoice status will change to <strong>PAID</strong> here automatically.
                    </p>
                  </div>
                </div>
                <div className="pay-workshop-address">
                  <div className="pay-workshop-address__row"><MapPin size={13}/> #24, Kissan Market, Sirhind Road, Patiala, Punjab 147001</div>
                  <div className="pay-workshop-address__row"><Clock size={13}/> Mon – Sat · 8:00 AM – 7:00 PM</div>
                </div>
                <button className="btn btn-primary pay-offline-confirm-btn" onClick={handleOfflineConfirm}>
                  <Check size={16}/> Confirm — I'll Pay at Workshop
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: confirm-delivery ── */}
        {step === 'confirm-delivery' && (
          <>
            <Header title="Pay on Delivery" backTo="choose"/>
            <div className="pay-form-body">
              <div className="pay-offline-screen">
                <div className="pay-offline-icon pay-offline-icon--blue"><Car size={34}/></div>
                <h3 className="pay-offline-title">Your Car Will Be Dropped to You</h3>
                <p className="pay-offline-desc">
                  Pay the driver by cash or QR when your car is delivered.
                  Invoice stays <strong>UNPAID</strong> until our driver confirms receipt.
                </p>
                {rec.address && (
                  <div className="pay-delivery-address">
                    <MapPin size={14}/> <strong>{rec.address}</strong>
                  </div>
                )}
                <div className="pay-offline-amount-chip">
                  <span>Amount Due on Delivery</span>
                  <strong>{inr(grand)}</strong>
                </div>
                <div className="pay-delivery-options">
                  <div className="pay-delivery-option"><Banknote size={20}/><span>Pay Cash</span></div>
                  <div className="pay-delivery-option-divider">or</div>
                  <div className="pay-delivery-option"><QrCode size={20}/><span>Scan QR Code</span></div>
                </div>
                <div className="pay-delivery-steps">
                  {[
                    { e:'🔧', t:'Service completed at our workshop' },
                    { e:'📞', t:'Driver calls you before arriving'  },
                    { e:'🚗', t:'Your Car will be delivered to your address'     },
                    { e:'💳', t:`Pay ${inr(grand)} — cash or QR`   },
                  ].map(({ e, t: label }) => (
                    <div key={label} className="pay-delivery-step">
                      <span className="pay-delivery-step__emoji">{e}</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary pay-offline-confirm-btn" onClick={handleOfflineConfirm}>
                  <Check size={16}/> Confirm — Pay on Delivery
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: processing ── */}
        {step === 'processing' && (
          <div className="pay-processing">
            <div className="pay-spinner"/>
            <p className="pay-processing__title">Confirming…</p>
            <p className="pay-processing__sub">Please wait a moment.</p>
          </div>
        )}

        {/* ── STEP: done ── */}
        {step === 'done' && (
          <div className="pay-done">
            <div className="pay-done__ring"><Check size={34} strokeWidth={2.5}/></div>
            <h3 className="pay-done__title">
              {txnId.startsWith('WRK')
                ? (isPickup ? 'Delivery Confirmed! 🚗' : 'Booking Noted! ✅')
                : 'Payment Successful! 🎉'}
            </h3>
            <p className="pay-done__amount">{inr(grand)}</p>

            {txnId.startsWith('WRK') ? (
              <div className="pay-done__workshop-msg">
                <AlertCircle size={15}/>
                Your invoice will show <strong>PAID</strong> once our team
                {isPickup ? ' delivery confirms receipt' : ' confirms payment at the counter'}.
                This usually takes a few minutes.
              </div>
            ) : (
              <p className="pay-done__method">Paid securely via Razorpay</p>
            )}

            <div className="pay-done__txn">
              <span>{txnId.startsWith('WRK') ? 'Reference Number' : 'Razorpay Payment ID'}</span>
              <code>{txnId}</code>
            </div>
            <p className="pay-done__hint">
              Keep this for your records. Call +91 12345 67890 for any queries.
            </p>
            <button
              className="btn btn-primary"
              style={{ width:'100%', justifyContent:'center' }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   FEEDBACK MODAL
══════════════════════════════════════════════════════════ */
const PROMPTS = ['','Poor — Very dissatisfied','Below average','Average','Good — Satisfied','Excellent! ⭐'];

function FeedbackModal({ rec, user, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hover,  setHover]  = useState(0);
  const [review, setReview] = useState('');
  const [done,   setDone]   = useState(false);

  const handleSubmit = () => {
    if (!rating) return;
    const fb = saveFeedback({
      bookingId: rec.id, invoiceId: rec.invoiceId || rec.id,
      userName: user?.name || 'Customer', vehicle: rec.vehicle,
      rating, review: review.trim() || PROMPTS[rating],
    });
    setDone(true);
    onSubmit(fb);
  };

  if (done) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()}>
        <div className="fb-done">
          <div className="fb-done__stars">
            {[1,2,3,4,5].map(n => (
              <Star key={n} size={26} fill={n <= rating ? '#F59E0B' : 'none'}
                color={n <= rating ? '#F59E0B' : 'var(--border)'} strokeWidth={1.5}/>
            ))}
          </div>
          <h3 className="fb-done__title">Thank you, {user?.name?.split(' ')[0]}!</h3>
          <p className="fb-done__text">Your review helps us serve you better.</p>
          <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }}
            onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()}>
        <div className="fb-modal__header">
          <h3 className="fb-modal__title">Rate Your Service</h3>
          <button className="inv-close-btn" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="fb-service-chip">
          {rec.services?.map(s => typeof s === 'object' ? s.name : s).join(', ')} · {rec.vehicle}
        </div>
        <div className="fb-stars-section">
          <p className="fb-stars-prompt">How would you rate this service?</p>
          <div className="fb-stars">
            {[1,2,3,4,5].map(n => (
              <button key={n} className="fb-star-btn"
                onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}>
                <Star size={36} fill={(hover || rating) >= n ? '#F59E0B' : 'none'}
                  color={(hover || rating) >= n ? '#F59E0B' : 'var(--border)'} strokeWidth={1.5}/>
              </button>
            ))}
          </div>
          {(hover || rating) > 0 && (
            <div className="fb-rating-label">{PROMPTS[hover || rating]}</div>
          )}
        </div>
        <label className="label" style={{ marginTop:16 }}>
          Write a Review{' '}
          <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10, color:'var(--text-muted)' }}>
            (optional)
          </span>
        </label>
        <textarea className="input" rows={4} style={{ resize:'vertical', minHeight:88 }}
          placeholder="Tell us what you loved or what we can improve…"
          value={review} onChange={e => setReview(e.target.value)}/>
        <div className="fb-actions">
          <button className="btn btn-ghost" onClick={onClose}>Skip</button>
          <button className="btn btn-primary" disabled={!rating} onClick={handleSubmit}>
            <MessageSquare size={14}/> Submit Review
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
export default function ServiceRecordsPage() {
  const { user, openLogin } = useAuth();
  const [search,    setSearch]    = useState('');
  const [expanded,  setExpanded]  = useState(null);
  const [viewRec,   setViewRec]   = useState(null);
  const [payRec,    setPayRec]    = useState(null);
  const [feedRec,   setFeedRec]   = useState(null);
  const [feedbacks, setFeedbacks] = useState({});
  const [records,   setRecords]   = useState([]);
  const [loading,   setLoading]   = useState(false);

  /* ── Load bookings + invoices from backend ─────────────
     Payment status = invoice.status === 'paid'
     This is ONLY set when admin clicks "Mark Paid" in BillingPage.
     No localStorage payment record drives the badge anymore.
  ────────────────────────────────────────────────────── */
  const loadRecords = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getMyBookings();
      const norm = (data || []).map(b => ({
        id:          b.id,
        invoiceId:   b.invoiceId   || null,
        date:        b.date        ? String(b.date) : '',
        vehicle:     b.vehicleMake ? `${b.vehicleMake} ${b.vehicleModel || ''}`.trim() : '',
        regNo:       b.regNumber   || '',
        fuelType:    b.fuelType    || '',
        services:    b.services    || [],
        mechanic:    b.mechanicName || '',
        status:      b.status      || 'pending',
        amount:      Number(b.estimatedAmount || 0),
        duration:    b.duration    || '',
        spareParts:  b.spareParts  || [],
        labour:      Number(b.labour || 0),
        gst:         18,
        notes:       b.additionalNotes || '',
        pickupType:  b.pickupType  || '',
        address:     b.address     || '',
        invoiceLoaded: false,
        invoicePaid:   false,   // ← driven by backend invoice.status, not localStorage
      }));

      /* Fetch real invoice data for bookings that have an invoiceId */
      const withInvoices = await Promise.all(
        norm.map(async b => {
          if (!b.invoiceId) return b;
          try {
            const inv = await getMyInvoice(b.id);
            if (!inv) return b;
            return {
              ...b,
              subtotal:    Number(inv.subtotal      || 0),
              gstAmount:   Number(inv.gstAmount     || 0),
              grandTotal:  Number(inv.grandTotal    || 0),
              amount:      Number(inv.subtotal      || b.amount),
              gst:         inv.gstPercent           || 18,
              labour:      Number(inv.labourCharges || 0),
              services:    inv.services             || b.services,
              spareParts:  inv.spareParts           || b.spareParts,
              mechanic:    inv.mechanicName         || b.mechanic,
              invoiceLoaded: true,
              /* KEY: payment status is ONLY from backend invoice.status */
              invoicePaid:   inv.status === 'paid',
              invoiceStatus: inv.status || '',
              paidAt:        inv.paidAt || inv.paymentDate || '',
            };
          } catch {
            return b;
          }
        })
      );

      setRecords(withInvoices);

      /* Load feedbacks from localStorage */
      const f = {};
      withInvoices.forEach(r => {
        const fb = getFeedbackByBooking(r.id);
        if (fb) f[r.id] = fb;
      });
      setFeedbacks(f);

    } catch (e) {
      console.error('Records load failed:', e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const totalSpent = records
    .filter(r => ['completed', 'COMPLETED'].includes(r.status))
    .reduce((s, r) => s + calcRecord(r).grand, 0);

  /* ── Gate ────────────────────────────────────────────── */
  if (!user) return (
    <div className="records-page" style={{ paddingTop:72 }}>
      <div className="booking-gate">
        <div className="booking-gate__icon"><FileText size={32}/></div>
        <h2 className="booking-gate__title">Sign In to View Records</h2>
        <p className="booking-gate__sub">See your full service history, invoices and receipts.</p>
        <div className="booking-gate__actions">
          <button className="btn btn-primary"  onClick={openLogin}>Sign In</button>
          <button className="btn btn-outline"  onClick={openLogin}>Create Account</button>
        </div>
      </div>
    </div>
  );

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return !q
      || r.id.toLowerCase().includes(q)
      || r.services.map(s => typeof s === 'object' ? s.name : s).join(' ').toLowerCase().includes(q)
      || r.vehicle.toLowerCase().includes(q);
  });

  return (
    <div className="records-page" style={{ paddingTop:72 }}>
      <div className="records-header">
        <div className="container">
          <h1 className="records-header__title">My Service Records</h1>
          <p className="records-header__sub">Complete history of your vehicle's care at AutoElite.</p>
        </div>
      </div>

      <div className="container section-sm">
        {/* Toolbar */}
        <div className="records-toolbar">
          <div className="records-search">
            <Search size={15}/>
            <input className="input" placeholder="Search booking ID, service or vehicle…"
              value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <div className="records-stats">
              <div className="records-stat">
                <span className="records-stat__value">{records.length}</span>
                <span className="records-stat__label">Total</span>
              </div>
              <div className="records-stat">
                <span className="records-stat__value">₹{totalSpent.toLocaleString('en-IN')}</span>
                <span className="records-stat__label">Spent</span>
              </div>
              <div className="records-stat">
                <span className="records-stat__value">
                  {records.filter(r => ['in-progress','in_progress','IN_PROGRESS','confirmed','CONFIRMED','waiting','WAITING'].includes(r.status)).length}
                </span>
                <span className="records-stat__label">Active</span>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={loadRecords} disabled={loading}
              title="Refresh records">
              <RefreshCw size={14} className={loading ? 'spinning' : ''}/>
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-muted)' }}>
            <div style={{ width:32, height:32, borderRadius:'50%',
              border:'3px solid var(--border)', borderTopColor:'var(--accent)',
              animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
            <p style={{ fontSize:13 }}>Loading your service records…</p>
          </div>
        )}

        {/* Records */}
        <div className="records-list">
          {!loading && filtered.map(rec => {
            const meta   = SM[rec.status] || SM['pending'];
            const isOpen = expanded === rec.id;
            const { grand } = calcRecord(rec);
            const hasFb  = !!feedbacks[rec.id];
            const hasInv = !!rec.invoiceId;
            const isDone = ['completed', 'COMPLETED'].includes(rec.status);
            /* ★ PAID status = backend invoice.status === 'paid' (set by admin) */
            const paid   = rec.invoicePaid;

            return (
              <div key={rec.id} className={`record-card card ${isOpen ? 'record-card--open' : ''}`}>
                <div className="record-card__main"
                  onClick={() => setExpanded(isOpen ? null : rec.id)}>
                  <div className="record-card__left">
                    <div className="record-card__id"><FileText size={13}/>{rec.id}</div>
                    <div className="record-card__services">
                      {rec.services.map((s, i) => (
                        <span key={i} className="record-service-tag">
                          {typeof s === 'object' ? s.name : s}
                        </span>
                      ))}
                    </div>
                    <div className="record-card__meta">
                      <span><Calendar size={11}/>{rec.date}</span>
                      {rec.duration && <span><Clock size={11}/>{rec.duration}</span>}
                    </div>
                  </div>
                  <div className="record-card__right">
                    <span className={`badge ${meta.badge}`}>
                      <meta.Icon size={11}/>{meta.label}
                    </span>
                    {/* Payment badge — only shown when admin marks paid */}
                    {hasInv && paid && (
                      <span className="badge badge-success"><Check size={10}/> Paid</span>
                    )}
                    {hasInv && !paid && isDone && (
                      <span className="badge badge-warning" style={{ fontSize:10 }}>
                        Unpaid
                      </span>
                    )}
                    <span className="record-card__amount">₹{grand.toLocaleString('en-IN')}</span>
                    <button className="record-card__expand">
                      {isOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="record-detail">
                    {rec.notes && (
                      <div className="record-notes">
                        <p className="label">Mechanic Notes</p>
                        <p className="record-notes__text">{rec.notes}</p>
                      </div>
                    )}

                    {isDone && (
                      <div className="record-actions">
                        {hasInv && (<>
                          <button className="btn btn-outline btn-sm" onClick={() => setViewRec(rec)}>
                            <Eye size={13}/> View Invoice
                          </button>
                          <button className="btn btn-outline btn-sm"
                            onClick={() => downloadInvoicePdf(rec, user)}>
                            <Download size={13}/> PDF
                          </button>
                          <button className="btn btn-ghost btn-sm"
                            onClick={() => printInvoicePdf(rec, user)}>
                            <Printer size={13}/> Print
                          </button>
                        </>)}

                        {/* Pay Now: only if invoice exists AND admin has NOT yet marked paid */}
                        {hasInv && !paid && (
                          <button className="btn btn-primary btn-sm" onClick={() => setPayRec(rec)}>
                            <CreditCard size={13}/> Pay Now
                          </button>
                        )}

                        {/* Paid tag: only when admin marks paid */}
                        {paid && (
                          <span className="record-paid-tag">
                            <Check size={12}/> Payment confirmed by workshop
                          </span>
                        )}

                        {/* Leave review: only after payment confirmed */}
                        {paid && !hasFb && (
                          <button className="btn btn-sm record-feedback-btn"
                            onClick={() => setFeedRec(rec)}>
                            <Star size={13}/> Leave Review
                          </button>
                        )}

                        {hasFb && (
                          <span className="record-reviewed-tag">
                            {[1,2,3,4,5].map(n => (
                              <Star key={n} size={11}
                                fill={n <= feedbacks[rec.id].rating ? '#F59E0B' : 'none'}
                                color={n <= feedbacks[rec.id].rating ? '#F59E0B' : 'var(--border)'}
                                strokeWidth={1.5}/>
                            ))}
                            <span>Reviewed</span>
                          </span>
                        )}

                        {!hasInv && (
                          <p className="record-no-inv">
                            Invoice will appear once the workshop publishes it.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {!loading && filtered.length === 0 && (
            <div className="records-empty">
              <FileText size={40}/>
              <p>
                {records.length === 0
                  ? 'No records yet. Book a service to get started.'
                  : 'No records match your search.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {viewRec && (
        <InvoiceModal
          rec={viewRec} user={user}
          onClose={() => setViewRec(null)}
          onPay={r => { setViewRec(null); setPayRec(r); }}
        />
      )}
      {payRec && (
        <PaymentModal
          rec={payRec} user={user}
          onClose={() => setPayRec(null)}
          onSuccess={() => {
            setPayRec(null);
            // Re-fetch records so payment status is up to date
            loadRecords();
          }}
        />
      )}
      {feedRec && (
        <FeedbackModal
          rec={feedRec} user={user}
          onClose={() => setFeedRec(null)}
          onSubmit={fb => setFeedbacks(f => ({ ...f, [feedRec.id]: fb }))}
        />
      )}
    </div>
  );
}